import { PodPanel } from "./PodPanel";
import {
  COMPARABLE_VEHICLE_TYPES,
  LOAD_VEHICLE_TYPES,
  VEHICLE_BODY_TYPES,
  VEHICLE_CAPABILITIES,
  emptyVehicleCapabilities,
  isVehicleTypeRangeValid,
  missingVehicleCapabilities,
} from "@loadsub/shared";
import { useEffect, useRef, useState, type FormEvent } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import {
  ApiError,
  addLoadStopDetails,
  acceptBid,
  closeLoad,
  createLoad,
  createVehicle,
  getFleet,
  listAddressBook,
  listAssignments,
  listGroups,
  listLoads,
  placeBid,
  updateLoad,
  withdrawBid,
  type AddressBookEntry,
  type CreateLoadInput,
  type GroupListItem,
  type LoadAssignment,
  type LoadStopInput,
  type LoadSummary,
  type TimescaleKind,
  type Vehicle,
} from "./api";
import { useAuth } from "./auth";
import { LoadConversation } from "./LoadConversation";
import { FilterDisclosure } from "./FilterDisclosure";
import { useRealtime } from "./realtime";
import { CompanyName } from "./FoundingMemberBadge";
import { LoadTrackingPanel } from "./LoadTrackingPanel";
const vehicleClasses: string[] = [
  "",
  ...LOAD_VEHICLE_TYPES.map((type) => type.value),
];
const vehicleSizeLadder: string[] = COMPARABLE_VEHICLE_TYPES.map(
  (type) => type.value,
);
type VehicleFilterMode = "any" | "upTo" | "exact";
type CoLoadFilter = "any" | "yes" | "no";
type BidStatusFilter =
  "all" | "pending" | "assigned" | "rejected" | "withdrawn";
const bodies: string[] = ["", ...VEHICLE_BODY_TYPES.map((body) => body.value)];
const timing: Array<[TimescaleKind, string]> = [
  ["asap", "ASAP"],
  ["today", "Today"],
  ["tomorrow", "Tomorrow"],
  ["this_week", "This week"],
  ["flexible", "Flexible"],
  ["custom", "Other"],
];
const stop = (action: LoadStopInput["action"]): LoadStopInput => ({
  action,
  goodsAction:
    action === "collect"
      ? "load"
      : action === "deliver"
        ? "unload"
        : "exchange",
  podRequired: action !== "collect",
  postcode: "",
});
const blank = (companyId: string): CreateLoadInput => ({
  companyId,
  groupIds: [],
  movementType: "goods",
  description: "",
  publicNotes: "",
  timescaleKind: "today",
  timescaleText: "Today",
  coLoadAllowed: false,
  isReturn: false,
  commercialMode: "open_bid",
  capabilityRequirements: {},
  stops: [stop("collect"), stop("deliver")],
});
export function LoadsPage({
  mode = "available",
}: {
  mode?: "available" | "posted" | "bids" | "assigned";
}) {
  const auth = useAuth();
  const realtime = useRealtime();
  const company = auth.session!.companies[0]!;
  const canManage = company.roles.some((r) => r === "owner" || r === "manager");
  const nav = useNavigate();
  const [searchParams] = useSearchParams();
  const requestedLoadId = searchParams.get("load") ?? undefined;
  const [loads, setLoads] = useState<LoadSummary[]>([]);
  const [assignments, setAssignments] = useState<LoadAssignment[]>([]);
  const [groups, setGroups] = useState<GroupListItem[]>([]);
  const [accessLoaded, setAccessLoaded] = useState(false);
  const [vehicles, setVehicles] = useState<Vehicle[]>([]);
  const [highlightedBidId, setHighlightedBidId] = useState<string>();
  const refreshTimer = useRef<number | undefined>(undefined);
  const highlightTimer = useRef<number | undefined>(undefined);
  const [showForm, setShowForm] = useState(false);
  const [editingLoadId, setEditingLoadId] = useState<string>();
  const [form, setForm] = useState<CreateLoadInput>(blank(company.companyId));
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [expandedLoadId, setExpandedLoadId] = useState<string>();
  const [vehicleFilterMode, setVehicleFilterMode] =
    useState<VehicleFilterMode>("any");
  const [upToVehicleClass, setUpToVehicleClass] = useState("LWB van");
  const [exactVehicleClasses, setExactVehicleClasses] = useState<string[]>([]);
  const [coLoadFilter, setCoLoadFilter] = useState<CoLoadFilter>("any");
  const [bidStatusFilter, setBidStatusFilter] =
    useState<BidStatusFilter>("all");
  async function refresh() {
    const [loadData, groupData, fleet, assignmentData] =
      await Promise.all([
        listLoads(company.companyId, mode),
        listGroups(company.companyId),
        getFleet(company.companyId),
        canManage ? listAssignments(company.companyId) : Promise.resolve([]),
      ]);
    setLoads(loadData.filter((load) => !load.publicNetwork || load.groups.length > 0));
    setGroups(groupData.filter((g) => g.membershipStatus === "active"));
    setVehicles(
      fleet.vehicles.filter(
        (v) => v.assetType === "powered" && v.status !== "vor",
      ),
    );
    setAssignments(assignmentData.filter((a) => a.loadStatus !== "completed"));
    setAccessLoaded(true);
  }
  useEffect(() => {
    void refresh().catch((e) => setError(message(e)));
  }, [company.companyId, mode]);
  useEffect(() => {
    if (requestedLoadId && loads.some((load) => load.id === requestedLoadId))
      setExpandedLoadId(requestedLoadId);
  }, [requestedLoadId, loads]);
  useEffect(() => {
    realtime.registerVisibleLoads(
      loads.map((load) => load.id),
      mode === "available",
    );
    return () => realtime.registerVisibleLoads([], false);
  }, [loads, mode, realtime.registerVisibleLoads]);
  useEffect(() => {
    const event = realtime.latest;
    if (!event) return;
    const isNewAvailableLoad =
      event.type === "load.published" && mode === "available";
    if (!isNewAvailableLoad && !loads.some((load) => load.id === event.loadId))
      return;
    window.clearTimeout(refreshTimer.current);
    refreshTimer.current = window.setTimeout(
      () => void refresh().catch((e) => setError(message(e))),
      350,
    );
    if (event.type === "bid.changed") {
      setHighlightedBidId(event.bidId);
      window.clearTimeout(highlightTimer.current);
      highlightTimer.current = window.setTimeout(
        () => setHighlightedBidId(undefined),
        2400,
      );
    }
    return () => window.clearTimeout(refreshTimer.current);
  }, [realtime.latest]);
  useEffect(
    () => () => {
      window.clearTimeout(refreshTimer.current);
      window.clearTimeout(highlightTimer.current);
    },
    [],
  );
  async function submit(event: FormEvent) {
    event.preventDefault();
    if (
      !isVehicleTypeRangeValid(
        form.requiredVehicleClass,
        form.maximumVehicleClass,
      )
    ) {
      setError(
        "Maximum vehicle cannot be smaller than the minimum vehicle requirement.",
      );
      return;
    }
    setBusy(true);
    setError("");
    try {
      if (editingLoadId) {
        await updateLoad(editingLoadId, form);
      } else await createLoad(form);
      setForm(blank(company.companyId));
      setEditingLoadId(undefined);
      setShowForm(false);
      if (mode === "posted") await refresh();
      else nav("/posted-loads");
    } catch (e) {
      setError(message(e));
    } finally {
      setBusy(false);
    }
  }
  const filteredLoads = (
    mode === "bids"
      ? loads.filter(
          (load) =>
            bidStatusFilter === "all" || bidStatus(load) === bidStatusFilter,
        )
      : mode !== "available"
        ? loads
        : loads.filter((load) => {
            if (coLoadFilter === "yes" && !load.coLoadAllowed) return false;
            if (coLoadFilter === "no" && load.coLoadAllowed) return false;
            if (vehicleFilterMode !== "any") {
              const required = load.requiredVehicleClass;
              if (required) {
                if (
                  vehicleFilterMode === "exact" &&
                  exactVehicleClasses.length > 0 &&
                  !exactVehicleClasses.includes(required)
                )
                  return false;
                if (vehicleFilterMode === "upTo") {
                  const selectedRank =
                      vehicleSizeLadder.indexOf(upToVehicleClass),
                    requiredRank = vehicleSizeLadder.indexOf(required);
                  if (
                    selectedRank < 0 ||
                    requiredRank < 0 ||
                    requiredRank > selectedRank
                  )
                    return false;
                  if (
                    load.maximumVehicleClass &&
                    isVehicleTypeRangeValid(
                      required,
                      load.maximumVehicleClass,
                    ) &&
                    selectedRank >
                      vehicleSizeLadder.indexOf(load.maximumVehicleClass)
                  )
                    return false;
                }
              }
            }
            return discoveryMatches(load, searchParams);
          })
  ).sort(discoverySort(searchParams));
  function editLoad(load: LoadSummary) {
    setForm({
      ...load,
      companyId: company.companyId,
      groupIds: load.groups.map((group) => group.id),
      stops: load.stops.map(
        ({
          order: _,
          lifecycleStatus: __,
          arrivedAt: ___,
          waitingStartedAt: ____,
          waitingEndedAt: _____,
          outcomeAt: ______,
          outcomeReason: _______,
          evidenceNotes: ________,
          ...stop
        }) => stop,
      ),
    });
    setEditingLoadId(load.id);
    setShowForm(true);
    window.scrollTo({ top: 0, behavior: "smooth" });
  }
  function cancelForm() {
    setShowForm(false);
    setEditingLoadId(undefined);
    setForm(blank(company.companyId));
  }
  function clearAvailableFilters() {
    setVehicleFilterMode("any");
    setExactVehicleClasses([]);
    setCoLoadFilter("any");
  }
  function toggleExactVehicle(value: string) {
    setExactVehicleClasses((current) =>
      current.includes(value)
        ? current.filter((item) => item !== value)
        : [...current, value],
    );
  }
  const title =
    mode === "posted"
      ? "Posted loads"
      : mode === "bids"
        ? "My bids"
        : mode === "assigned"
          ? "Assigned loads"
          : "Available loads";
  const intro =
    mode === "posted"
      ? "Loads posted by your company."
      : mode === "bids"
        ? "Loads your company has bid on."
        : mode === "assigned"
          ? "Loads assigned to your company, including confidential stop details."
          : "Live work shared within your private groups.";
  return (
    <>
      <header className="team-header">
        <div>
          <h1 className="h3 mb-1">{title}</h1>
          <p className="text-secondary mb-0">{intro}</p>
        </div>
        {canManage && mode !== "bids" && mode !== "assigned" && (
          <button
            className="btn btn-success"
            onClick={() => (showForm ? cancelForm() : setShowForm(true))}
          >
            {showForm ? "Cancel" : "Post load"}
          </button>
        )}
      </header>
      {canManage && mode === "posted" && (
        <section className="team-panel load-import-promo mb-3">
          <div>
            <strong>Lots of viable loads already in a spreadsheet?</strong>
            <p>Upload XLSX or CSV, map it once and publish them together.</p>
          </div>
          <Link className="btn btn-outline-light" to="/load-import">
            Import spreadsheet
          </Link>
        </section>
      )}
      {error && <div className="alert alert-danger py-2">{error}</div>}
      {showForm && (
        <LoadForm
          form={form}
          setForm={setForm}
          groups={groups}
          accessLoaded={accessLoaded}
          submit={submit}
          busy={busy}
          editing={Boolean(editingLoadId)}
        />
      )}
      {mode === "available" && (
        <AvailableLoadFilters
          total={loads.length}
          shown={filteredLoads.length}
          vehicleMode={vehicleFilterMode}
          setVehicleMode={setVehicleFilterMode}
          upToClass={upToVehicleClass}
          setUpToClass={setUpToVehicleClass}
          exactClasses={exactVehicleClasses}
          toggleExact={toggleExactVehicle}
          coLoad={coLoadFilter}
          setCoLoad={setCoLoadFilter}
          clear={clearAvailableFilters}
        />
      )}{" "}
      {mode === "bids" && (
        <BidStatusFilters
          loads={loads}
          value={bidStatusFilter}
          set={setBidStatusFilter}
        />
      )}
      <div
        className={`load-feed${mode === "available" || mode === "bids" || mode === "assigned" ? " available-load-feed" : ""}`}
      >
        {filteredLoads.map((load) => (
          <LoadCard
            key={load.id}
            load={load}
            companyId={company.companyId}
            vehicles={vehicles}
            assignments={assignments}
            canBid={canManage}
            refresh={refresh}
            setError={setError}
            highlightedBidId={highlightedBidId}
            showPosterPod={mode === "posted"}
            compact={
              mode === "available" || mode === "bids" || mode === "assigned"
            }
            bidSummary={mode === "bids"}
            {...(mode === "posted" ? { onEdit: editLoad } : {})}
            expanded={mode === "posted" || expandedLoadId === load.id}
            onToggle={() =>
              setExpandedLoadId((current) =>
                current === load.id ? undefined : load.id,
              )
            }
          />
        ))}
      </div>
      {filteredLoads.length === 0 && !showForm && (
        <section className="empty-panel">
          <h2>
            {mode === "posted"
              ? "No loads posted yet"
              : mode === "bids"
                ? loads.length
                  ? "No bids match this filter"
                  : "No bids placed"
                : mode === "assigned"
                  ? "No assigned loads"
                  : "No available loads"}
          </h2>
          <p>
            {mode === "posted"
              ? "Post a load to share it with one or more of your private groups."
              : mode === "bids"
                ? loads.length
                  ? "Choose another bid status to see more."
                  : "Loads appear here after your company places a bid."
                : mode === "assigned"
                  ? "Accepted work will appear here with the confidential route and stop details."
                  : "Loads shared into your groups will appear here."}
          </p>
        </section>
      )}
    </>
  );
}
function LoadForm({
  form,
  setForm,
  groups,
  accessLoaded,
  submit,
  busy,
  editing,
}: {
  form: CreateLoadInput;
  setForm: (v: CreateLoadInput) => void;
  groups: GroupListItem[];
  accessLoaded: boolean;
  submit: (e: FormEvent) => void;
  busy: boolean;
  editing: boolean;
}) {
  const [addressBook, setAddressBook] = useState<AddressBookEntry[]>([]);
  useEffect(() => {
    void listAddressBook(form.companyId)
      .then(setAddressBook)
      .catch(() => setAddressBook([]));
  }, [form.companyId]);
  const update = <K extends keyof CreateLoadInput>(
    key: K,
    value: CreateLoadInput[K],
  ) => setForm({ ...form, [key]: value });
  function updateStop(index: number, value: LoadStopInput) {
    const stops = [...form.stops];
    stops[index] = value;
    update("stops", stops);
  }
  function copyAddress(index: number, id: string) {
    const entry = addressBook.find((item) => item.id === id);
    if (!entry) return;
    const current = form.stops[index]!;
    updateStop(index, {
      ...current,
      postcode: entry.postcode,
      siteName: entry.siteName,
      address: entry.fullAddress,
      ...(entry.contactName ? { contactName: entry.contactName } : {}),
      ...(entry.contactTelephone
        ? { contactTelephone: entry.contactTelephone }
        : {}),
      ...(entry.contactEmail ? { contactEmail: entry.contactEmail } : {}),
      ...(entry.accessInstructions
        ? { accessInstructions: entry.accessInstructions }
        : {}),
      ...(entry.openingHours ? { openingHours: entry.openingHours } : {}),
    });
  }
  function removeStop(index: number) {
    update(
      "stops",
      form.stops.filter((_, i) => i !== index),
    );
  }
  function setMovement(type: "goods" | "trailer") {
    if (type === "trailer")
      setForm({
        ...form,
        movementType: type,
        trailerCouplingType: form.trailerCouplingType ?? "semi",
        coLoadAllowed: false,
        requiredVehicleClass: "Tractor unit",
        requiredBodyType: undefined,
        maximumVehicleClass: undefined,
        stops: form.stops.map((item, index) => ({
          ...item,
          operations: item.operations?.length
            ? item.operations
            : index === 0
              ? [{ action: "collect_trailer", loadedState: "unknown" }]
              : index === form.stops.length - 1
                ? [{ action: "drop_trailer" }]
                : [],
        })),
      });
    else
      setForm({
        ...form,
        movementType: type,
        trailerCouplingType: undefined,
        trailerLoadedState: undefined,
        trailerBodyType: undefined,
        stops: form.stops.map((item) => ({ ...item, operations: undefined })),
      });
  }
  function selectTiming(kind: TimescaleKind, label: string) {
    setForm({
      ...form,
      timescaleKind: kind,
      timescaleText: kind === "custom" ? "" : label,
    });
  }
  return (
    <section className="team-panel load-form-panel mb-4">
      <div className="load-form-heading">
        <div>
          <h2 className="h5 mb-1">{editing ? "Edit load" : "Post a load"}</h2>
          <p className="text-secondary small mb-0">
            Only the public information below is visible before a carrier is
            assigned.
          </p>
        </div>
        <span className="privacy-note">
          Avoid customer or site names in public notes
        </span>
      </div>
      <form onSubmit={submit}>
        <fieldset>
          <legend>Movement</legend>
          <div className="load-form-grid">
            <label>
              <span>Movement type</span>
              <select
                className="form-select"
                value={form.movementType}
                onChange={(event) =>
                  setMovement(event.target.value as "goods" | "trailer")
                }
              >
                <option value="goods">Goods movement</option>
                <option value="trailer">Trailer movement</option>
              </select>
            </label>
            {form.movementType === "trailer" && (
              <>
                <label>
                  <span>Trailer coupling</span>
                  <select
                    className="form-select"
                    value={form.trailerCouplingType ?? "semi"}
                    onChange={(event) =>
                      setForm({
                        ...form,
                        trailerCouplingType: event.target.value as
                          "semi" | "drawbar",
                        requiredVehicleClass:
                          event.target.value === "semi"
                            ? "Tractor unit"
                            : "Drawbar rigid",
                      })
                    }
                  >
                    <option value="semi">Semi-trailer / fifth wheel</option>
                    <option value="drawbar">Drawbar trailer</option>
                  </select>
                </label>
                <label>
                  <span>Starting condition</span>
                  <select
                    className="form-select"
                    value={form.trailerLoadedState ?? "unknown"}
                    onChange={(event) =>
                      update(
                        "trailerLoadedState",
                        event.target.value as "loaded" | "empty" | "unknown",
                      )
                    }
                  >
                    <option value="unknown">Not yet known</option>
                    <option value="loaded">Loaded</option>
                    <option value="empty">Empty</option>
                  </select>
                </label>
                <label>
                  <span>
                    Trailer body/type <small>optional</small>
                  </span>
                  <input
                    className="form-control"
                    value={form.trailerBodyType ?? ""}
                    onChange={(event) =>
                      update("trailerBodyType", event.target.value || undefined)
                    }
                  />
                </label>
              </>
            )}
          </div>
          {form.movementType === "trailer" && (
            <p className="alert alert-info py-2 mb-0">
              LoadSub will require a compatible{" "}
              {form.trailerCouplingType === "drawbar"
                ? "drawbar-capable rigid"
                : "tractor unit"}
              . Third-party trailers remain custody records and are not added to
              the carrier's fleet.
            </p>
          )}
        </fieldset>
        <fieldset>
          <legend>Choose where to share</legend>
          <div className="group-picker">
            {groups.map((item) => (
              <label key={item.group.id}>
                <input
                  type="checkbox"
                  checked={form.groupIds.includes(item.group.id)}
                  onChange={(e) =>
                    update(
                      "groupIds",
                      e.target.checked
                        ? [...form.groupIds, item.group.id]
                        : form.groupIds.filter((id) => id !== item.group.id),
                    )
                  }
                />
                <span>{item.group.name}</span>
              </label>
            ))}
          </div>
          {!accessLoaded ? (
            <p className="text-secondary small">
              Loading your sharing options…
            </p>
          ) : (
            groups.length === 0 && (
              <p className="alert alert-warning py-2">
                Your company must be an active member of a private group before
                posting.
              </p>
            )
          )}
        </fieldset>
        <fieldset>
          <legend>Route</legend>
          <div className="stop-list">
            {form.stops.map((s, index) => (
              <div className="stop-row" key={index}>
                <span className="stop-number" aria-label={`Stop ${index + 1}`}>
                  {index + 1}
                </span>
                <label className="stop-field stop-action">
                  <span>Activity</span>
                  <select
                    className="form-select"
                    value={s.action}
                    onChange={(e) =>
                      updateStop(index, {
                        ...s,
                        action: e.target.value as LoadStopInput["action"],
                      })
                    }
                  >
                    <option value="collect">Collect</option>
                    <option value="deliver">Deliver</option>
                    <option value="both">Collect & Deliver</option>
                  </select>
                </label>
                {form.movementType === "goods" && (
                  <label className="stop-field">
                    <span>Goods action</span>
                    <select
                      className="form-select"
                      value={
                        s.goodsAction ??
                        (s.action === "collect"
                          ? "load"
                          : s.action === "deliver"
                            ? "unload"
                            : "exchange")
                      }
                      onChange={(e) =>
                        updateStop(index, {
                          ...s,
                          goodsAction: e.target.value as NonNullable<
                            LoadStopInput["goodsAction"]
                          >,
                        })
                      }
                    >
                      <option value="load">Load goods</option>
                      <option value="unload">Unload goods</option>
                      <option value="exchange">Unload and load</option>
                      <option value="none">No goods handling</option>
                    </select>
                  </label>
                )}
                <label className="stop-field stop-postcode">
                  <span>Postcode</span>
                  <input
                    className="form-control text-uppercase"
                    required
                    maxLength={20}
                    value={s.postcode}
                    onChange={(e) =>
                      updateStop(index, { ...s, postcode: e.target.value })
                    }
                  />
                </label>
                <label className="form-check stop-pod">
                  <input
                    className="form-check-input"
                    type="checkbox"
                    checked={Boolean(s.podRequired)}
                    onChange={(e) =>
                      updateStop(index, { ...s, podRequired: e.target.checked })
                    }
                  />
                  <span>POD required at this stop</span>
                </label>
                {form.movementType === "trailer" && (
                  <TrailerStopOperations
                    stop={s}
                    set={(value) => updateStop(index, value)}
                  />
                )}{" "}
                {form.stops.length > 2 && (
                  <button
                    className="stop-remove"
                    type="button"
                    aria-label="Remove stop"
                    onClick={() => removeStop(index)}
                  >
                    ×
                  </button>
                )}
                <details>
                  <summary>Confidential stop details</summary>
                  <div className="stop-private">
                    <small className="stop-privacy">
                      Only the assigned carrier can see the details copied into
                      this load.
                    </small>
                    {addressBook.length > 0 && (
                      <label className="stop-address-book">
                        <span>Copy a saved site</span>
                        <select
                          className="form-select"
                          value=""
                          onChange={(e) => copyAddress(index, e.target.value)}
                        >
                          <option value="">
                            Choose from private address book…
                          </option>
                          {addressBook.map((entry) => (
                            <option key={entry.id} value={entry.id}>
                              {entry.siteName} · {entry.postcode}
                            </option>
                          ))}
                        </select>
                      </label>
                    )}
                    <label>
                      <span>Site name</span>
                      <input
                        className="form-control"
                        value={s.siteName ?? ""}
                        onChange={(e) =>
                          updateStop(index, {
                            ...s,
                            siteName: e.target.value || undefined,
                          })
                        }
                      />
                    </label>
                    <label className="stop-address">
                      <span>Full address</span>
                      <textarea
                        className="form-control"
                        rows={2}
                        value={s.address ?? ""}
                        onChange={(e) =>
                          updateStop(index, {
                            ...s,
                            address: e.target.value || undefined,
                          })
                        }
                      />
                    </label>
                    <label>
                      <span>Contact name</span>
                      <input
                        className="form-control"
                        value={s.contactName ?? ""}
                        onChange={(e) =>
                          updateStop(index, {
                            ...s,
                            contactName: e.target.value || undefined,
                          })
                        }
                      />
                    </label>
                    <label>
                      <span>Contact telephone</span>
                      <input
                        className="form-control"
                        type="tel"
                        value={s.contactTelephone ?? ""}
                        onChange={(e) =>
                          updateStop(index, {
                            ...s,
                            contactTelephone: e.target.value || undefined,
                          })
                        }
                      />
                    </label>
                    <label>
                      <span>Contact email</span>
                      <input
                        className="form-control"
                        type="email"
                        value={s.contactEmail ?? ""}
                        onChange={(e) =>
                          updateStop(index, {
                            ...s,
                            contactEmail: e.target.value || undefined,
                          })
                        }
                      />
                    </label>
                    <label className="stop-access">
                      <span>Access instructions</span>
                      <textarea
                        className="form-control"
                        rows={2}
                        value={s.accessInstructions ?? ""}
                        onChange={(e) =>
                          updateStop(index, {
                            ...s,
                            accessInstructions: e.target.value || undefined,
                          })
                        }
                      />
                    </label>
                    <label className="stop-hours">
                      <span>Opening / access times</span>
                      <textarea
                        className="form-control"
                        rows={2}
                        value={s.openingHours ?? ""}
                        onChange={(e) =>
                          updateStop(index, {
                            ...s,
                            openingHours: e.target.value || undefined,
                          })
                        }
                      />
                    </label>
                    <label className="stop-instructions">
                      <span>Stop instructions</span>
                      <textarea
                        className="form-control"
                        rows={2}
                        value={s.instructions ?? ""}
                        onChange={(e) =>
                          updateStop(index, {
                            ...s,
                            instructions: e.target.value || undefined,
                          })
                        }
                      />
                    </label>
                  </div>
                </details>
              </div>
            ))}
          </div>
          <div className="route-actions">
            <button
              className="btn btn-sm btn-outline-light"
              type="button"
              onClick={() => update("stops", [...form.stops, stop("deliver")])}
            >
              Add stop
            </button>
            <label className="form-check">
              <input
                className="form-check-input"
                type="checkbox"
                checked={form.isReturn}
                onChange={(e) => update("isReturn", e.target.checked)}
              />
              <span>Return load</span>
            </label>
          </div>
        </fieldset>
        <fieldset>
          <legend>Commercial terms</legend>
          <div className="load-form-grid">
            <label>
              <span>Pricing</span>
              <select
                className="form-select"
                value={form.commercialMode}
                onChange={(e) =>
                  update(
                    "commercialMode",
                    e.target.value as CreateLoadInput["commercialMode"],
                  )
                }
              >
                <option value="open_bid">Open bidding</option>
                <option value="fixed_with_counter">
                  Fixed payment (counter-offers allowed)
                </option>
              </select>
            </label>
            {form.commercialMode === "fixed_with_counter" && (
              <label>
                <span>Fixed payment (£)</span>
                <input
                  className="form-control"
                  type="number"
                  min="0.01"
                  step="0.01"
                  required
                  value={
                    form.fixedPaymentPence ? form.fixedPaymentPence / 100 : ""
                  }
                  onChange={(e) =>
                    update(
                      "fixedPaymentPence",
                      e.target.value
                        ? Math.round(Number(e.target.value) * 100)
                        : undefined,
                    )
                  }
                />
              </label>
            )}
            <label>
              <span>
                Trailer number <small>optional</small>
              </span>
              <input
                className="form-control"
                value={form.trailerNumber ?? ""}
                onChange={(e) =>
                  update("trailerNumber", e.target.value || undefined)
                }
              />
            </label>
            <label>
              <span>
                Load closes <small>optional</small>
              </span>
              <input
                className="form-control"
                type="datetime-local"
                min={new Date().toISOString().slice(0, 16)}
                value={form.expiresAt?.slice(0, 16) ?? ""}
                onChange={(e) =>
                  update(
                    "expiresAt",
                    e.target.value
                      ? new Date(e.target.value).toISOString()
                      : undefined,
                  )
                }
              />
            </label>
            <label>
              <span>
                Job reference <small>optional</small>
              </span>
              <input
                className="form-control"
                value={form.jobReference ?? ""}
                onChange={(e) =>
                  update("jobReference", e.target.value || undefined)
                }
              />
            </label>
            <label className="wide">
              <span>
                Job instructions <small>optional</small>
              </span>
              <textarea
                className="form-control"
                rows={2}
                value={form.jobInstructions ?? ""}
                onChange={(e) =>
                  update("jobInstructions", e.target.value || undefined)
                }
              />
            </label>
          </div>
        </fieldset>
        <fieldset>
          <legend>Load details</legend>
          <div className="load-form-grid">
            <label>
              <span>Description</span>
              <input
                className="form-control"
                required
                maxLength={300}
                placeholder="e.g. 2 pallets of machine parts"
                value={form.description}
                onChange={(e) => update("description", e.target.value)}
              />
            </label>
            <label className="wide">
              <span>Public notes</span>
              <textarea
                className="form-control"
                required
                rows={3}
                maxLength={5000}
                value={form.publicNotes}
                onChange={(e) => update("publicNotes", e.target.value)}
              />
            </label>
          </div>
          <div className="timing-picker">
            {timing.map(([kind, label]) => (
              <button
                type="button"
                className={form.timescaleKind === kind ? "active" : ""}
                key={kind}
                onClick={() => selectTiming(kind, label)}
              >
                {label}
              </button>
            ))}
          </div>
          {form.timescaleKind === "custom" && (
            <input
              className="form-control mt-2"
              required
              maxLength={200}
              placeholder="Enter collection/delivery timescale"
              value={form.timescaleText}
              onChange={(e) => update("timescaleText", e.target.value)}
            />
          )}
        </fieldset>
        <details className="optional-details">
          <summary>Vehicle, dimensions and optional detail</summary>
          <div className="load-form-grid mt-3">
            <MinimumVehicleSelect
              maximum={form.maximumVehicleClass}
              value={form.requiredVehicleClass}
              set={(v) => update("requiredVehicleClass", v)}
            />
            <MaximumVehicleSelect
              minimum={form.requiredVehicleClass}
              value={form.maximumVehicleClass}
              set={(v) => update("maximumVehicleClass", v)}
            />
            <Select
              label="Body / loadspace"
              value={form.requiredBodyType}
              values={bodies}
              set={(v) => update("requiredBodyType", v)}
            />
            <Num
              label="Quantity"
              value={form.quantity}
              set={(v) => update("quantity", v)}
            />
            <Num
              label="Weight (kg)"
              value={form.weightKg}
              set={(v) => update("weightKg", v)}
            />
            <label>
              <span>
                Approximate mileage <small>optional</small>
              </span>
              <div className="input-group">
                <input
                  className="form-control"
                  type="number"
                  min="0.1"
                  step="0.1"
                  value={form.posterMileage ?? ""}
                  onChange={(e) =>
                    update(
                      "posterMileage",
                      e.target.value ? Number(e.target.value) : undefined,
                    )
                  }
                />
                <button
                  className="btn btn-outline-light"
                  type="button"
                  onClick={() =>
                    void estimateRouteMiles(
                      form.stops.map((item) => item.postcode),
                    )
                      .then((miles) => update("posterMileage", miles))
                      .catch(() =>
                        window.alert(
                          "Mileage could not be estimated. Enter it manually.",
                        ),
                      )
                  }
                >
                  Estimate
                </button>
              </div>
            </label>
            <Num
              label="Length (mm)"
              value={form.lengthMm}
              set={(v) => update("lengthMm", v)}
            />
            <Num
              label="Width (mm)"
              value={form.widthMm}
              set={(v) => update("widthMm", v)}
            />
            <Num
              label="Height (mm)"
              value={form.heightMm}
              set={(v) => update("heightMm", v)}
            />
            <label className="wide">
              <span>Access restrictions</span>
              <textarea
                className="form-control"
                rows={2}
                placeholder="e.g. maximum 7.5t, low bridge or restricted turning space"
                value={form.accessRestrictions ?? ""}
                onChange={(e) =>
                  update("accessRestrictions", e.target.value || undefined)
                }
              />
            </label>
          </div>
          {!isVehicleTypeRangeValid(
            form.requiredVehicleClass,
            form.maximumVehicleClass,
          ) && (
            <p className="vehicle-range-warning">
              Maximum vehicle cannot be smaller than the minimum vehicle
              requirement.
            </p>
          )}
        </details>
        <CapabilityRequirements
          value={form.capabilityRequirements}
          set={(value) => update("capabilityRequirements", value)}
        />
        <label className="co-load-choice">
          <input
            type="checkbox"
            checked={form.coLoadAllowed}
            onChange={(e) => update("coLoadAllowed", e.target.checked)}
          />
          <span>
            <strong>Co-loading allowed</strong>
            <small>Carrier may carry compatible goods at the same time.</small>
          </span>
        </label>
        <button
          className="btn btn-success mt-3"
          disabled={
            busy ||
            !accessLoaded ||
            form.groupIds.length === 0
          }
        >
          {busy
            ? editing
              ? "Saving…"
              : "Publishing…"
            : editing
              ? "Save changes"
              : "Publish load"}
        </button>
      </form>
    </section>
  );
}
function CapabilityRequirements({
  value,
  set,
}: {
  value: CreateLoadInput["capabilityRequirements"];
  set: (value: CreateLoadInput["capabilityRequirements"]) => void;
}) {
  return (
    <fieldset className="capability-requirements">
      <legend>Equipment requirements</legend>
      <small>
        Choose Preferred or Required only where it matters. Everything else
        remains N/A.
      </small>
      <div className="capability-requirement-grid">
        {VEHICLE_CAPABILITIES.map((capability) => (
          <label key={capability.id}>
            <span>{capability.label}</span>
            <select
              className="form-select form-select-sm"
              value={value[capability.id] ?? ""}
              onChange={(event) => {
                const next = { ...value };
                const level = event.target.value as
                  "preferred" | "required" | "";
                if (level) next[capability.id] = level;
                else delete next[capability.id];
                set(next);
              }}
            >
              <option value="">N/A</option>
              <option value="preferred">Preferred</option>
              <option value="required">Required</option>
            </select>
          </label>
        ))}
      </div>
    </fieldset>
  );
}
function bidStatus(load: LoadSummary): Exclude<BidStatusFilter, "all"> {
  return load.myBid?.status === "accepted"
    ? "assigned"
    : (load.myBid?.status ?? "pending");
}
function bidStatusLabel(load: LoadSummary) {
  const status = bidStatus(load);
  return status[0]!.toUpperCase() + status.slice(1);
}
function BidStatusFilters({
  loads,
  value,
  set,
}: {
  loads: LoadSummary[];
  value: BidStatusFilter;
  set: (value: BidStatusFilter) => void;
}) {
  const filters: Array<[BidStatusFilter, string]> = [
    ["all", "All"],
    ["pending", "Pending"],
    ["assigned", "Assigned"],
    ["rejected", "Rejected"],
    ["withdrawn", "Withdrawn"],
  ];
  return (
    <nav className="bid-status-filters" aria-label="Filter bids by status">
      {filters.map(([status, label]) => {
        const count =
          status === "all"
            ? loads.length
            : loads.filter((load) => bidStatus(load) === status).length;
        return (
          <button
            type="button"
            className={value === status ? "active" : ""}
            aria-pressed={value === status}
            onClick={() => set(status)}
            key={status}
          >
            <span>{label}</span>
            <b>{count}</b>
          </button>
        );
      })}
    </nav>
  );
}
function AvailableLoadFilters({
  total,
  shown,
  vehicleMode,
  setVehicleMode,
  upToClass,
  setUpToClass,
  exactClasses,
  toggleExact,
  coLoad,
  setCoLoad,
  clear,
}: {
  total: number;
  shown: number;
  vehicleMode: VehicleFilterMode;
  setVehicleMode: (value: VehicleFilterMode) => void;
  upToClass: string;
  setUpToClass: (value: string) => void;
  exactClasses: string[];
  toggleExact: (value: string) => void;
  coLoad: CoLoadFilter;
  setCoLoad: (value: CoLoadFilter) => void;
  clear: () => void;
}) {
  const [params, setParams] = useSearchParams();
  const value = (key: string) => params.get(key) ?? "";
  function set(key: string, next: string) {
    const copy = new URLSearchParams(params);
    next ? copy.set(key, next) : copy.delete(key);
    setParams(copy, { replace: true });
  }
  function reset() {
    clear();
    setParams(new URLSearchParams(), { replace: true });
    sessionStorage.removeItem("loadsub:search-origin");
  }
  async function manual() {
    const postcode = value("origin").trim();
    if (!postcode) return;
    try {
      const response = await fetch(
          `https://api.postcodes.io/postcodes/${encodeURIComponent(postcode)}`,
        ),
        data = (await response.json()) as any;
      if (!response.ok || !data.result) throw new Error();
      sessionStorage.setItem(
        "loadsub:search-origin",
        JSON.stringify({
          lat: data.result.latitude,
          lon: data.result.longitude,
          source: "manual",
          label: postcode.toUpperCase(),
        }),
      );
      set("near", "manual");
    } catch {
      window.alert(
        "That postcode could not be located. You can still use the area filters.",
      );
    }
  }
  function gps() {
    navigator.geolocation.getCurrentPosition(
      (position) => {
        sessionStorage.setItem(
          "loadsub:search-origin",
          JSON.stringify({
            lat: position.coords.latitude,
            lon: position.coords.longitude,
            source: "gps",
            label: "Current location",
          }),
        );
        set("near", "gps");
      },
      () =>
        window.alert("Location was not available. Enter a postcode instead."),
      { enableHighAccuracy: false, maximumAge: 300000, timeout: 10000 },
    );
  }
  const activeCount =
    [...params.values()].filter(Boolean).length +
    (vehicleMode !== "any" ? 1 : 0) +
    (coLoad !== "any" ? 1 : 0) +
    (vehicleMode === "exact" ? exactClasses.length : 0);
  return (
    <FilterDisclosure
      activeCount={activeCount}
      summary={`${shown} of ${total} load${total === 1 ? "" : "s"} shown`}
    >
      <section
        className="available-load-filters"
        aria-label="Available load filters"
      >
        <div className="available-filter-heading">
          <strong>Find suitable loads</strong>
          <span>
            {shown} of {total} load{total === 1 ? "" : "s"}
          </span>
        </div>
        <div className="available-filter-row">
          <label>
            <span>Search</span>
            <input
              className="form-control form-control-sm"
              value={value("q")}
              onChange={(event) => set("q", event.target.value)}
              placeholder="Route, load or poster"
            />
          </label>
          <label>
            <span>Group</span>
            <input
              className="form-control form-control-sm"
              value={value("group")}
              onChange={(event) => set("group", event.target.value)}
              placeholder="Group name"
            />
          </label>
          <label>
            <span>Collection area</span>
            <input
              className="form-control form-control-sm text-uppercase"
              value={value("from")}
              onChange={(event) => set("from", event.target.value)}
              placeholder="e.g. LS"
            />
          </label>
          <label>
            <span>Delivery area</span>
            <input
              className="form-control form-control-sm text-uppercase"
              value={value("to")}
              onChange={(event) => set("to", event.target.value)}
              placeholder="e.g. B"
            />
          </label>
          <label>
            <span>Timescale</span>
            <select
              className="form-select form-select-sm"
              value={value("when")}
              onChange={(event) => set("when", event.target.value)}
            >
              <option value="">Any</option>
              {timing.map(([kind, label]) => (
                <option key={kind} value={kind}>
                  {label}
                </option>
              ))}
            </select>
          </label>
          <label>
            <span>Poster</span>
            <input
              className="form-control form-control-sm"
              value={value("poster")}
              onChange={(event) => set("poster", event.target.value)}
              placeholder="Company"
            />
          </label>
          <label>
            <span>Sort</span>
            <select
              className="form-select form-select-sm"
              value={value("sort")}
              onChange={(event) => set("sort", event.target.value)}
            >
              <option value="">Newest</option>
              <option value="price">Price</option>
              <option value="mileage">Mileage</option>
              <option value="rate">£/mile</option>
              <option value="distance">Nearest collection</option>
            </select>
          </label>
        </div>
        <div className="available-filter-row">
          <label>
            <span>Vehicle filtering</span>
            <select
              className="form-select form-select-sm"
              value={vehicleMode}
              onChange={(event) =>
                setVehicleMode(event.target.value as VehicleFilterMode)
              }
            >
              <option value="any">Any vehicle requirement</option>
              <option value="upTo">My vehicle can take smaller loads</option>
              <option value="exact">Exact requirements</option>
            </select>
          </label>
          {vehicleMode === "upTo" && (
            <label>
              <span>My vehicle size</span>
              <select
                className="form-select form-select-sm"
                value={upToClass}
                onChange={(event) => setUpToClass(event.target.value)}
              >
                {vehicleSizeLadder.map((item) => (
                  <option key={item}>{item}</option>
                ))}
              </select>
            </label>
          )}
          <label className="co-load-only">
            <input
              type="checkbox"
              checked={coLoad === "yes"}
              onChange={(event) =>
                setCoLoad(event.target.checked ? "yes" : "any")
              }
            />
            <span>Co-load only</span>
          </label>
          <label>
            <span>Search origin</span>
            <input
              className="form-control form-control-sm text-uppercase"
              value={value("origin")}
              onChange={(event) => set("origin", event.target.value)}
              placeholder="Postcode"
            />
          </label>
          <label>
            <span>Radius</span>
            <select
              className="form-select form-select-sm"
              value={value("radius")}
              onChange={(event) => set("radius", event.target.value)}
            >
              <option value="">Any</option>
              {[10, 25, 50, 100, 200].map((item) => (
                <option key={item} value={item}>
                  {item} miles
                </option>
              ))}
            </select>
          </label>
          <button
            className="btn btn-sm btn-outline-light"
            type="button"
            onClick={() => void manual()}
          >
            Use postcode
          </button>
          <button
            className="btn btn-sm btn-outline-light"
            type="button"
            onClick={gps}
          >
            Use my location
          </button>
          <button
            className="btn btn-sm btn-outline-light"
            type="button"
            onClick={reset}
          >
            Clear filters
          </button>
        </div>
        {vehicleMode === "exact" && (
          <div className="exact-vehicle-filter">
            {vehicleClasses.filter(Boolean).map((item) => (
              <label key={item}>
                <input
                  type="checkbox"
                  checked={exactClasses.includes(item)}
                  onChange={() => toggleExact(item)}
                />
                <span>{item}</span>
              </label>
            ))}
          </div>
        )}
        <small className="filter-help">
          Location is requested only when you choose it. GPS coordinates stay in
          this browser tab and are not sent to or stored by LoadSub. Manual
          postcode lookup uses Postcodes.io; use area filters if lookup is
          unavailable.
        </small>
      </section>
    </FilterDisclosure>
  );
}
function LoadCard({
  load,
  companyId,
  vehicles,
  assignments,
  canBid,
  refresh,
  setError,
  highlightedBidId,
  showPosterPod = false,
  compact = false,
  bidSummary = false,
  expanded = true,
  onToggle,
  onEdit,
}: {
  load: LoadSummary;
  companyId: string;
  vehicles: Vehicle[];
  assignments: LoadAssignment[];
  canBid: boolean;
  refresh: () => Promise<void>;
  setError: (value: string) => void;
  highlightedBidId: string | undefined;
  showPosterPod?: boolean;
  compact?: boolean;
  bidSummary?: boolean;
  expanded?: boolean;
  onToggle?: () => void;
  onEdit?: (load: LoadSummary) => void;
}) {
  const requirements = [
    load.quantity && `${load.quantity} item${load.quantity === 1 ? "" : "s"}`,
    load.weightKg && `${load.weightKg.toLocaleString("en-GB")} kg`,
    load.requiredVehicleClass,
    load.requiredBodyType,
    load.maximumVehicleClass && `Maximum ${load.maximumVehicleClass}`,
  ].filter(Boolean);
  const route = load.stops.map((s) => s.postcode).join(" → ");
  const routeSummary =
    load.stops.length > 2
      ? `${load.stops[0]?.postcode} → ${load.stops[load.stops.length - 1]?.postcode}`
      : route;
  return (
    <article
      className={`load-card-live${compact ? " load-card-compact" : ""}${expanded ? " is-expanded" : ""}`}
    >
      {compact ? (
        <button
          type="button"
          className="available-load-summary"
          aria-expanded={expanded}
          onClick={onToggle}
        >
          <span className="available-load-route">
            <strong>{routeSummary}</strong>
            {load.stops.length > 2 && <small>{load.stops.length} stops</small>}
          </span>
          <span className="available-load-description">{load.description}</span>
          <span className="available-load-requirement">
            {load.myBid?.status === "accepted"
              ? load.myBid.vehicleRegistration
              : load.requiredVehicleClass || "Vehicle not specified"}
            {load.myBid?.status === "accepted" ? (
              <small>
                {load.myBid.vehicleClass} · {load.myBid.bodyType}
              </small>
            ) : (
              load.requiredBodyType && <small>{load.requiredBodyType}</small>
            )}
          </span>
          <span className="available-load-facts">
            {load.weightKg && (
              <small>{load.weightKg.toLocaleString("en-GB")} kg</small>
            )}
            {load.quantity && (
              <small>
                {load.quantity} item{load.quantity === 1 ? "" : "s"}
              </small>
            )}
            <small>{load.coLoadAllowed ? "Co-load" : "No co-load"}</small>
          </span>
          <span className="available-load-poster">
            <small>{load.posterTradingName}</small>
            <b>
              {bidSummary && load.myBid
                ? `${money(load.myBid.amountPence / 100)} · ${bidStatusLabel(load)}`
                : load.timescaleText}
            </b>
          </span>
          <span className="available-load-chevron" aria-hidden="true">
            {expanded ? "−" : "+"}
          </span>
        </button>
      ) : (
        <header>
          <div>
            <h2>{route}</h2>
            <span className="timing-chip">{load.timescaleText}</span>
            {load.movementType === "trailer" && (
              <span className="assigned-chip">
                Trailer movement ·{" "}
                {load.trailerCouplingType === "drawbar"
                  ? "Drawbar"
                  : "Semi-trailer"}
              </span>
            )}
            {load.status !== "open" && (
              <span className="assigned-chip">
                {load.status.replace("_", " ")}
              </span>
            )}
          </div>
          <span className="load-groups">
            {load.groups.map((g) => g.name).join(", ")}
          </span>
        </header>
      )}
      {expanded && (
        <div className={compact ? "available-load-detail" : undefined}>
          {compact && (
            <div className="available-load-detail-heading">
              <span className="load-groups">
                {load.groups.map((g) => g.name).join(", ")}
              </span>
              <small>
                Posted {new Date(load.publishedAt).toLocaleString("en-GB")}
              </small>
            </div>
          )}
          <div className="load-reference">
            <strong>{load.publicReference}</strong>
            {load.expiresAt && load.status === "open" && (
              <small>
                Closes {new Date(load.expiresAt).toLocaleString("en-GB")}
              </small>
            )}
          </div>
          <h3>{load.description}</h3>
          {load.fixedPaymentPence && (
            <p className="fixed-payment">
              <strong>
                Fixed payment: {money(load.fixedPaymentPence / 100)}
              </strong>{" "}
              · Counter-offers allowed
            </p>
          )}
          {(load.trailerNumber || load.jobReference) && (
            <p>
              {load.trailerNumber && `Trailer ${load.trailerNumber}`}
              {load.trailerNumber && load.jobReference ? " · " : ""}
              {load.jobReference && `Reference ${load.jobReference}`}
            </p>
          )}
          {load.jobInstructions && (
            <p className="load-notes">
              <strong>Instructions:</strong> {load.jobInstructions}
            </p>
          )}
          {requirements.length > 0 && <p>{requirements.join(" · ")}</p>}
          {Object.keys(load.capabilityRequirements).length > 0 && (
            <div className="capability-tags">
              {VEHICLE_CAPABILITIES.filter(
                (capability) => load.capabilityRequirements[capability.id],
              ).map((capability) => (
                <em key={capability.id}>
                  {capability.label} ·{" "}
                  {load.capabilityRequirements[capability.id]}
                </em>
              ))}
            </div>
          )}
          <p className="load-notes">{load.publicNotes}</p>
          {load.postedByCurrentCompany && load.status === "open" && (
            <div className="load-edit-actions">
              <button
                className="btn btn-sm btn-outline-light"
                type="button"
                onClick={() => onEdit?.(load)}
              >
                Edit load
              </button>
              <button
                className="btn btn-sm btn-outline-danger"
                type="button"
                onClick={() => {
                  if (
                    !window.confirm(
                      `Close ${load.publicReference}? Pending bids will be rejected.`,
                    )
                  )
                    return;
                  void closeLoad(load.id, companyId)
                    .then(refresh)
                    .catch((error) => setError(message(error)));
                }}
              >
                Close unassigned load
              </button>
            </div>
          )}
          {load.postedByCurrentCompany && (
            <AddMissingStopDetails
              load={load}
              companyId={companyId}
              refresh={refresh}
              setError={setError}
            />
          )}
          {load.status !== "open" &&
            !load.postedByCurrentCompany &&
            load.myBid?.status === "accepted" && (
              <AssignedStops stops={load.stops} />
            )}
          {load.postedByCurrentCompany ? (
            <PosterBids
              load={load}
              companyId={companyId}
              refresh={refresh}
              setError={setError}
              highlightedBidId={highlightedBidId}
            />
          ) : (
            canBid && (
              <BidForm
                load={load}
                companyId={companyId}
                vehicles={vehicles}
                assignments={assignments}
                refresh={refresh}
                setError={setError}
              />
            )
          )}{" "}
          {canBid &&
            (load.postedByCurrentCompany ||
              load.status === "open" ||
              Boolean(load.myBid)) && (
              <LoadConversation load={load} companyId={companyId} />
            )}
          {load.postedByCurrentCompany && load.status !== "open" && (
            <LoadTrackingPanel
              companyId={companyId}
              loadId={load.id}
              posterView
            />
          )}
          {showPosterPod &&
            load.postedByCurrentCompany &&
            load.status !== "open" && (
              <PodPanel
                loadId={load.id}
                companyId={companyId}
                canManage={canBid}
              />
            )}{" "}
          {load.status !== "open" && (
            <Link
              className="btn btn-sm btn-outline-warning"
              to={"/case/" + load.id}
            >
              Cancellation / dispute
            </Link>
          )}
          <footer>
            <div>
              {load.isReturn && <span>↩ Return</span>}
              <span>
                {load.coLoadAllowed ? "Co-load allowed" : "No co-load"}
              </span>
              {load.posterMileage && <span>{load.posterMileage} miles</span>}
            </div>
            <small>
              {load.postedByCurrentCompany
                ? "Posted by your company"
                : `Posted by ${load.posterTradingName}`}{" "}
              · {new Date(load.publishedAt).toLocaleString("en-GB")}
            </small>
          </footer>
        </div>
      )}
    </article>
  );
}
function AddMissingStopDetails({
  load,
  companyId,
  refresh,
  setError,
}: {
  load: LoadSummary;
  companyId: string;
  refresh: () => Promise<void>;
  setError: (value: string) => void;
}) {
  const missing = load.stops.some(
    (stop) =>
      !stop.siteName ||
      !stop.address ||
      !stop.contactName ||
      !stop.contactTelephone ||
      !stop.contactEmail ||
      !stop.accessInstructions ||
      !stop.openingHours ||
      !stop.instructions,
  );
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [drafts, setDrafts] = useState(() =>
    load.stops.map((stop) => ({
      order: stop.order,
      siteName: "",
      address: "",
      contactName: "",
      contactTelephone: "",
      contactEmail: "",
      accessInstructions: "",
      openingHours: "",
      instructions: "",
    })),
  );
  if (!missing) return null;
  function update(
    order: number,
    key:
      | "siteName"
      | "address"
      | "contactName"
      | "contactTelephone"
      | "contactEmail"
      | "accessInstructions"
      | "openingHours"
      | "instructions",
    value: string,
  ) {
    setDrafts((current) =>
      current.map((item) =>
        item.order === order ? { ...item, [key]: value } : item,
      ),
    );
  }
  async function save(event: FormEvent) {
    event.preventDefault();
    setBusy(true);
    setError("");
    try {
      await addLoadStopDetails(
        load.id,
        companyId,
        drafts.map((item) => ({
          order: item.order,
          ...(item.siteName.trim() ? { siteName: item.siteName } : {}),
          ...(item.address.trim() ? { address: item.address } : {}),
          ...(item.contactName.trim() ? { contactName: item.contactName } : {}),
          ...(item.contactTelephone.trim()
            ? { contactTelephone: item.contactTelephone }
            : {}),
          ...(item.contactEmail.trim()
            ? { contactEmail: item.contactEmail }
            : {}),
          ...(item.accessInstructions.trim()
            ? { accessInstructions: item.accessInstructions }
            : {}),
          ...(item.openingHours.trim()
            ? { openingHours: item.openingHours }
            : {}),
          ...(item.instructions.trim()
            ? { instructions: item.instructions }
            : {}),
        })),
      );
      setOpen(false);
      await refresh();
    } catch (error) {
      setError(message(error));
    } finally {
      setBusy(false);
    }
  }
  return (
    <section className="add-stop-details">
      <div>
        <strong>Missing stop details</strong>
        <button
          className="btn btn-sm btn-warning"
          type="button"
          onClick={() => setOpen((value) => !value)}
        >
          {open ? "Close" : "Add details"}
        </button>
      </div>
      {open && (
        <form onSubmit={save}>
          {load.stops.map((stop, index) => (
            <section
              className="missing-stop-fieldset"
              role="group"
              aria-labelledby={`missing-stop-${load.id}-${stop.order}`}
              key={stop.order}
            >
              <div
                className="missing-stop-heading"
                id={`missing-stop-${load.id}-${stop.order}`}
              >
                <span>Stop {stop.order}</span>
                <strong>{stop.postcode}</strong>
              </div>
              {(
                [
                  ["siteName", "Site name"],
                  ["address", "Full address"],
                  ["contactName", "Contact name"],
                  ["contactTelephone", "Contact telephone"],
                  ["contactEmail", "Contact email"],
                  ["accessInstructions", "Access instructions"],
                  ["openingHours", "Opening / access times"],
                  ["instructions", "Stop instructions"],
                ] as const
              ).map(([key, label]) => (
                <label key={key}>
                  <span>{label}</span>
                  {stop[key] ? (
                    <div className="provided-stop-detail">{stop[key]}</div>
                  ) : key === "address" ||
                    key === "accessInstructions" ||
                    key === "openingHours" ||
                    key === "instructions" ? (
                    <textarea
                      className="form-control"
                      rows={2}
                      value={drafts[index]?.[key] ?? ""}
                      onChange={(event) =>
                        update(stop.order, key, event.target.value)
                      }
                    />
                  ) : (
                    <input
                      className="form-control"
                      type={
                        key === "contactTelephone"
                          ? "tel"
                          : key === "contactEmail"
                            ? "email"
                            : "text"
                      }
                      value={drafts[index]?.[key] ?? ""}
                      onChange={(event) =>
                        update(stop.order, key, event.target.value)
                      }
                    />
                  )}
                </label>
              ))}
            </section>
          ))}
          <button className="btn btn-sm btn-primary" disabled={busy}>
            {busy ? "Saving…" : "Save added details"}
          </button>
        </form>
      )}
    </section>
  );
}
const bidEnhancements = new Map<
  string,
  { assumesCoLoad: boolean; expiresAt: string }
>();
function BidForm(props: {
  load: LoadSummary;
  companyId: string;
  vehicles: Vehicle[];
  assignments: LoadAssignment[];
  refresh: () => Promise<void>;
  setError: (value: string) => void;
}) {
  const existing = props.load.myBid;
  const [assumesCoLoad, setAssumesCoLoad] = useState(
    Boolean(existing?.assumesCoLoad),
  );
  const [expiresAt, setExpiresAt] = useState(
    existing?.expiresAt?.slice(0, 16) ?? "",
  );
  const [insurance, setInsurance] = useState<{
    allowed: boolean;
    findings: Array<{
      groupId: string;
      groupName: string;
      insuranceType: string;
      label: string;
      level: string;
      message: string;
    }>;
  }>();
  useEffect(() => {
    void import("./api")
      .then((api) => api.getLoadInsuranceCheck(props.load.id, props.companyId))
      .then(setInsurance)
      .catch(() => setInsurance(undefined));
  }, [props.load.id, props.companyId]);
  bidEnhancements.set(props.load.id, { assumesCoLoad, expiresAt });
  return (
    <>
      {insurance && insurance.findings.length > 0 && (
        <section
          className={
            insurance.allowed
              ? "alert alert-warning py-2"
              : "alert alert-danger py-2"
          }
        >
          <strong>
            {insurance.allowed
              ? "Insurance warnings — this bid is still allowed"
              : "Resolve mandatory insurance requirements before bidding"}
          </strong>
          {insurance.findings.map((f) => (
            <div key={f.groupId + f.insuranceType}>
              {f.groupName} · {f.label}: {f.message} ({f.level})
            </div>
          ))}
        </section>
      )}
      <section className="bid-enhancements" aria-label="Bid commercial options">
        {props.load.coLoadAllowed && (
          <label className="form-check">
            <input
              className="form-check-input"
              type="checkbox"
              checked={assumesCoLoad}
              onChange={(event) => setAssumesCoLoad(event.target.checked)}
            />
            <span className="form-check-label">
              This price assumes co-loading
            </span>
          </label>
        )}
        <label>
          <span>
            Bid expiry <small>optional</small>
          </span>
          <input
            className="form-control"
            type="datetime-local"
            min={new Date().toISOString().slice(0, 16)}
            value={expiresAt}
            onChange={(event) => setExpiresAt(event.target.value)}
          />
        </label>
      </section>
      <LegacyBidForm {...props} />
    </>
  );
}
function LegacyBidForm({
  load,
  companyId,
  vehicles,
  assignments,
  refresh,
  setError,
}: {
  load: LoadSummary;
  companyId: string;
  vehicles: Vehicle[];
  assignments: LoadAssignment[];
  refresh: () => Promise<void>;
  setError: (value: string) => void;
}) {
  if (load.myBid?.status === "accepted")
    return (
      <div className="assignment-summary assignment-won">
        <strong>Assigned to your company</strong>
        <span>
          {money(load.myBid.amountPence / 100)} ·{" "}
          {load.myBid.vehicleRegistration} · {load.myBid.vehicleClass}
        </span>
      </div>
    );
  if (load.myBid?.status === "rejected")
    return (
      <div className="assignment-summary">
        <strong>Another bid was accepted</strong>
        <span>Your bid was {money(load.myBid.amountPence / 100)}</span>
      </div>
    );
  const existing = load.myBid?.status === "pending" ? load.myBid : undefined;
  const [open, setOpen] = useState(Boolean(existing));
  const [amount, setAmount] = useState(
    existing
      ? (existing.amountPence / 100).toFixed(2)
      : load.fixedPaymentPence
        ? (load.fixedPaymentPence / 100).toFixed(2)
        : "",
  );
  const [collectionEta, setCollectionEta] = useState(
    existing?.proposedCollectionEta?.slice(0, 16) ?? "",
  );
  const [deliveryEta, setDeliveryEta] = useState(
    existing?.proposedDeliveryEta?.slice(0, 16) ?? "",
  );
  const [conditions, setConditions] = useState(existing?.conditions ?? "");
  const [mileage, setMileage] = useState(
    existing
      ? String(existing.estimatedMileage)
      : load.posterMileage
        ? String(load.posterMileage)
        : "",
  );
  const [vehicleId, setVehicleId] = useState(
    existing?.vehicleId ?? vehicles[0]?.id ?? "",
  );
  const [addingVehicle, setAddingVehicle] = useState(false);
  const [registration, setRegistration] = useState("");
  const [vehicleClass, setVehicleClass] = useState("LWB van");
  const [bodyType, setBodyType] = useState("Panel van");
  const [quickCapabilities, setQuickCapabilities] = useState(
    emptyVehicleCapabilities(),
  );
  const [busy, setBusy] = useState(false);
  const [showAssignments, setShowAssignments] = useState(false);
  const compatibleVehicles =
    load.movementType === "trailer"
      ? vehicles.filter((vehicle) =>
          load.trailerCouplingType === "semi"
            ? vehicle.couplingType === "fifth_wheel" ||
              vehicle.couplingType === "both"
            : vehicle.couplingType === "drawbar" ||
              vehicle.couplingType === "both",
        )
      : vehicles;
  const selectedVehicle = compatibleVehicles.find(
    (vehicle) => vehicle.id === vehicleId,
  );
  const selectedAssignments = assignments.filter(
    (assignment) => assignment.poweredVehicleId === vehicleId,
  );
  const missingRequiredIds = selectedVehicle
    ? missingVehicleCapabilities(
        load.capabilityRequirements,
        selectedVehicle.bodyType,
        selectedVehicle.capabilities,
        "required",
      )
    : [];
  const missingPreferredIds = selectedVehicle
    ? missingVehicleCapabilities(
        load.capabilityRequirements,
        selectedVehicle.bodyType,
        selectedVehicle.capabilities,
        "preferred",
      )
    : [];
  const missingRequired = VEHICLE_CAPABILITIES.filter((capability) =>
    missingRequiredIds.includes(capability.id),
  );
  const missingPreferred = VEHICLE_CAPABILITIES.filter((capability) =>
    missingPreferredIds.includes(capability.id),
  );
  const ppm =
    Number(amount) > 0 && Number(mileage) > 0
      ? Number(amount) / Number(mileage)
      : 0;
  async function save(event: FormEvent) {
    event.preventDefault();
    setBusy(true);
    setError("");
    const extras = bidEnhancements.get(load.id);
    try {
      await placeBid(load.id, {
        companyId,
        vehicleId,
        amountPence: Math.round(Number(amount) * 100),
        estimatedMileage: Number(mileage),
        ...(collectionEta
          ? { proposedCollectionEta: new Date(collectionEta).toISOString() }
          : {}),
        ...(deliveryEta
          ? { proposedDeliveryEta: new Date(deliveryEta).toISOString() }
          : {}),
        ...(conditions.trim() ? { conditions: conditions.trim() } : {}),
        respondsToFixedOffer: load.commercialMode === "fixed_with_counter",
        ...(extras?.assumesCoLoad ? { assumesCoLoad: true } : {}),
        ...(extras?.expiresAt
          ? { expiresAt: new Date(extras.expiresAt).toISOString() }
          : {}),
      });
      await refresh();
    } catch (e) {
      setError(message(e));
    } finally {
      setBusy(false);
    }
  }
  async function addVehicle() {
    if (!registration.trim()) return;
    setBusy(true);
    setError("");
    try {
      const vehicle = await createVehicle(companyId, {
        assetType: "powered",
        registration,
        vehicleClass,
        bodyType,
        status: "active",
        capabilities: quickCapabilities,
      });
      setVehicleId(vehicle.id);
      setAddingVehicle(false);
      setRegistration("");
      setQuickCapabilities(emptyVehicleCapabilities());
      await refresh();
    } catch (e) {
      setError(message(e));
    } finally {
      setBusy(false);
    }
  }
  async function withdraw() {
    if (!window.confirm("Withdraw this bid?")) return;
    setBusy(true);
    setError("");
    try {
      await withdrawBid(load.id, companyId);
      setOpen(false);
      await refresh();
    } catch (e) {
      setError(message(e));
    } finally {
      setBusy(false);
    }
  }
  if (!open)
    return (
      <div className="bid-summary">
        <div>
          {load.myBid?.status === "withdrawn" ? (
            <strong>Bid withdrawn</strong>
          ) : (
            <strong>No bid placed</strong>
          )}
        </div>
        <button
          className="btn btn-sm btn-primary"
          onClick={() => setOpen(true)}
        >
          {load.myBid ? "Bid again" : "Place bid"}
        </button>
        {vehicles.length === 0 && (
          <small>You can add a vehicle without leaving this load.</small>
        )}
      </div>
    );
  return (
    <form className="bid-form" onSubmit={save}>
      {addingVehicle && (
        <div className="quick-vehicle">
          <div>
            <strong>Add a vehicle</strong>
            <small>Only the essentials—full details can be added later.</small>
          </div>
          <label>
            <span>Registration</span>
            <input
              className="form-control text-uppercase"
              required
              value={registration}
              onChange={(e) => setRegistration(e.target.value)}
            />
          </label>
          <label>
            <span>Vehicle class</span>
            <select
              className="form-select"
              value={vehicleClass}
              onChange={(e) => setVehicleClass(e.target.value)}
            >
              {vehicleClasses.filter(Boolean).map((value) => (
                <option key={value}>{value}</option>
              ))}
            </select>
          </label>
          <label>
            <span>Body / loadspace</span>
            <select
              className="form-select"
              value={bodyType}
              onChange={(e) => setBodyType(e.target.value)}
            >
              {bodies
                .filter((value) => value && value !== "Other")
                .map((value) => (
                  <option key={value}>{value}</option>
                ))}
            </select>
          </label>
          <div className="capability-grid quick-capabilities">
            {VEHICLE_CAPABILITIES.map((capability) => (
              <label className="form-check" key={capability.id}>
                <input
                  className="form-check-input"
                  type="checkbox"
                  checked={quickCapabilities[capability.id]}
                  onChange={(event) =>
                    setQuickCapabilities((current) => ({
                      ...current,
                      [capability.id]: event.target.checked,
                    }))
                  }
                />
                <span className="form-check-label">{capability.label}</span>
              </label>
            ))}
          </div>
          <div>
            <button
              className="btn btn-sm btn-primary"
              type="button"
              disabled={busy || !registration.trim()}
              onClick={() => void addVehicle()}
            >
              {busy ? "Adding…" : "Add and select"}
            </button>
            <button
              className="btn btn-sm btn-outline-light"
              type="button"
              onClick={() => setAddingVehicle(false)}
            >
              Cancel
            </button>
          </div>
        </div>
      )}
      <div className="bid-fields">
        <label>
          <span>
            {load.commercialMode === "fixed_with_counter"
              ? "Offer / counter-offer (£)"
              : "Bid (£)"}
          </span>
          <input
            className="form-control"
            type="number"
            min="0.01"
            step="0.01"
            required
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
          />
        </label>
        <label>
          <span>Estimated mileage</span>
          <input
            className="form-control"
            type="number"
            min="0.1"
            step="0.1"
            required
            value={mileage}
            onChange={(e) => setMileage(e.target.value)}
          />
        </label>
        <label>
          <span>Vehicle</span>
          <div className="bid-vehicle-choice">
            <select
              className="form-select"
              required
              value={vehicleId}
              onChange={(e) => setVehicleId(e.target.value)}
            >
              <option value="">Select vehicle</option>
              {compatibleVehicles.map((v) => {
                const count = assignments.filter(
                  (a) => a.poweredVehicleId === v.id,
                ).length;
                return (
                  <option value={v.id} key={v.id}>
                    {v.registration}
                    {count ? ` [${count}]` : ""} · {v.vehicleClass} ·{" "}
                    {v.bodyType}
                  </option>
                );
              })}
            </select>
            <button
              className="btn btn-sm btn-outline-light"
              type="button"
              onClick={() => setAddingVehicle(true)}
            >
              Add vehicle
            </button>
          </div>
        </label>
      </div>
      <div className="bid-fields bid-counter-fields">
        <label>
          <span>
            Proposed collection ETA <small>optional</small>
          </span>
          <input
            className="form-control"
            type="datetime-local"
            value={collectionEta}
            onChange={(e) => setCollectionEta(e.target.value)}
          />
        </label>
        <label>
          <span>
            Proposed delivery ETA <small>optional</small>
          </span>
          <input
            className="form-control"
            type="datetime-local"
            value={deliveryEta}
            onChange={(e) => setDeliveryEta(e.target.value)}
          />
        </label>
        <label>
          <span>
            Conditions / note <small>optional</small>
          </span>
          <input
            className="form-control"
            maxLength={2000}
            value={conditions}
            onChange={(e) => setConditions(e.target.value)}
          />
        </label>
      </div>
      {selectedAssignments.length > 0 && (
        <div className="vehicle-assignment-summary">
          <button
            className="btn btn-sm btn-link"
            type="button"
            onClick={() => setShowAssignments((value) => !value)}
          >
            {showAssignments ? "Hide" : `View ${selectedAssignments.length}`}{" "}
            current assignment{selectedAssignments.length === 1 ? "" : "s"}
          </button>
          {showAssignments && (
            <div className="vehicle-assignment-panel">
              {selectedAssignments.map((assignment) => (
                <article key={assignment.id}>
                  <strong>{assignment.routePostcodes.join(" → ")}</strong>
                  <span>{assignment.description}</span>
                  <small>
                    {assignment.timescaleText} ·{" "}
                    {assignment.loadStatus === "assigned"
                      ? "Not started"
                      : assignment.loadStatus === "in_transit"
                        ? assignment.confidentialStops.some(
                            (stop) => stop.lifecycleStatus === "arrived",
                          )
                          ? `At stop ${assignment.confidentialStops.find((stop) => stop.lifecycleStatus === "arrived")?.order} · waiting`
                          : `In progress · ${assignment.confidentialStops.filter((stop) => ["completed", "partial", "failed"].includes(stop.lifecycleStatus)).length}/${assignment.confidentialStops.length} stops finished`
                        : assignment.loadStatus}
                  </small>
                </article>
              ))}
            </div>
          )}
        </div>
      )}
      {selectedVehicle &&
        (missingRequired.length > 0 || missingPreferred.length > 0) && (
          <div
            className={
              missingRequired.length
                ? "alert alert-danger py-2"
                : "alert alert-warning py-2"
            }
          >
            {missingRequired.length > 0 && (
              <span>
                Missing required:{" "}
                {missingRequired.map((item) => item.label).join(", ")}
              </span>
            )}
            {missingPreferred.length > 0 && (
              <span>
                Missing preferred:{" "}
                {missingPreferred.map((item) => item.label).join(", ")}
              </span>
            )}
          </div>
        )}
      <div className="bid-actions">
        <strong>
          {ppm ? `${money(ppm)} / mile` : "Enter price and mileage"}
        </strong>
        <button
          className="btn btn-sm btn-primary"
          disabled={busy || !vehicleId || missingRequired.length > 0}
        >
          {busy ? "Saving…" : existing ? "Update bid" : "Submit bid"}
        </button>
        {existing && (
          <button
            className="btn btn-sm btn-outline-danger"
            type="button"
            disabled={busy}
            onClick={() => void withdraw()}
          >
            Withdraw
          </button>
        )}
        <button
          className="btn btn-sm btn-outline-light"
          type="button"
          onClick={() => setOpen(false)}
        >
          Cancel
        </button>
      </div>
    </form>
  );
}
function PosterBids({
  load,
  companyId,
  refresh,
  setError,
  highlightedBidId,
}: {
  load: LoadSummary;
  companyId: string;
  refresh: () => Promise<void>;
  setError: (value: string) => void;
  highlightedBidId: string | undefined;
}) {
  const bids = load.bids ?? [];
  const [busy, setBusy] = useState<string>();
  async function accept(bid: NonNullable<LoadSummary["bids"]>[number]) {
    const detail = `Accept ${bid.bidderTradingName}\n\n${money(bid.amountPence / 100)} · ${bid.estimatedMileage} miles\n${bid.vehicleRegistration} · ${bid.vehicleClass} · ${bid.bodyType}\n\nThis rejects every other bid and assigns the load.`;
    if (!window.confirm(detail)) return;
    setBusy(bid.id);
    setError("");
    try {
      await acceptBid(load.id, bid.id, companyId);
      await refresh();
    } catch (e) {
      setError(message(e));
    } finally {
      setBusy(undefined);
    }
  }
  return (
    <section className="received-bids">
      <div className="bid-panel-heading">
        <strong>
          {bids.length} bid{bids.length === 1 ? "" : "s"} received
        </strong>
        {load.status === "assigned" ? (
          <span className="assigned-chip">Carrier assigned</span>
        ) : (
          bids.length > 0 && <small>Lowest price first</small>
        )}
      </div>
      {bids.map((bid) => (
        <div
          className={`received-bid${bid.id === highlightedBidId ? " bid-flash" : ""}${bid.status === "accepted" ? " bid-accepted" : ""}`}
          key={bid.id}
        >
          <div>
            <strong>{money(bid.amountPence / 100)}</strong>
            <small>
              {money(bid.pricePerMilePence / 100)} / mile ·{" "}
              {bid.estimatedMileage} miles
            </small>
          </div>
          <div>
            <CompanyName
              name={bid.bidderTradingName}
              foundingEarlyAdopter={Boolean(bid.foundingEarlyAdopter)}
            />
            <small>
              {bid.vehicleRegistration} · {bid.vehicleClass} · {bid.bodyType}
            </small>
            <BidCapabilityFit load={load} bid={bid} />
          </div>
          <div className="bid-decision">
            {bid.status === "pending" && load.status === "open" ? (
              <button
                className="btn btn-sm btn-success"
                disabled={Boolean(busy)}
                onClick={() => void accept(bid)}
              >
                {busy === bid.id ? "Assigning…" : "Accept bid"}
              </button>
            ) : (
              <span className={"bid-status bid-status-" + bid.status}>
                {bid.status === "accepted" ? "Accepted" : "Not selected"}
              </span>
            )}
          </div>
        </div>
      ))}
    </section>
  );
}
function BidCapabilityFit({
  load,
  bid,
}: {
  load: LoadSummary;
  bid: NonNullable<LoadSummary["bids"]>[number];
}) {
  const missingIds = missingVehicleCapabilities(
    load.capabilityRequirements,
    bid.bodyType,
    bid.vehicleCapabilities,
    "preferred",
  );
  const missingPreferred = VEHICLE_CAPABILITIES.filter((capability) =>
    missingIds.includes(capability.id),
  );
  const vat = bid.vatRegistered ? Math.round(bid.amountPence * 0.2) : 0;
  return (
    <div className="bid-commercial-detail">
      {missingPreferred.length > 0 && (
        <small className="text-warning">
          Preferred equipment missing:{" "}
          {missingPreferred.map((item) => item.label).join(", ")}
        </small>
      )}
      <small>
        {bid.vatRegistered
          ? `VAT registered${bid.vatNumber ? ` · ${bid.vatNumber}` : ""} · ${money(bid.amountPence / 100)} ex VAT · ${money((bid.amountPence + vat) / 100)} inc VAT`
          : `Not VAT registered · ${money(bid.amountPence / 100)} total`}
      </small>
      {bid.assumesCoLoad && <small>Price assumes co-loading</small>}
      {bid.expiresAt && (
        <small>Expires {new Date(bid.expiresAt).toLocaleString("en-GB")}</small>
      )}
      {bid.reconfirmationRequired && (
        <small className="text-warning">
          Reconfirmation required after load amendment
        </small>
      )}
    </div>
  );
}
function AssignedStops({ stops }: { stops: LoadSummary["stops"] }) {
  return (
    <section className="assigned-stops">
      <strong>Route and stop details</strong>
      {stops.map((stop) => (
        <article
          className={`assigned-stop-card lifecycle-${stop.lifecycleStatus ?? "pending"}`}
          key={stop.order}
        >
          <div
            className="assigned-stop-marker"
            aria-label={`Stop ${stop.order}`}
          >
            <span aria-hidden="true">📍</span>
            <strong>{stop.order}</strong>
          </div>
          <div className="assigned-stop-content">
            <h4>
              {stop.postcode}{" "}
              {stop.lifecycleStatus && <small>{stop.lifecycleStatus}</small>}
            </h4>
            <dl>
              <div>
                <dt>Site name</dt>
                <dd className={stop.siteName ? "" : "missing-detail"}>
                  {stop.siteName || "Not provided"}
                </dd>
              </div>
              <div>
                <dt>Full address</dt>
                <dd className={stop.address ? "" : "missing-detail"}>
                  {stop.address || "Not provided"}
                </dd>
              </div>
              <div>
                <dt>Contact name</dt>
                <dd>{stop.contactName || "Not provided"}</dd>
              </div>
              <div>
                <dt>Contact telephone</dt>
                <dd>{stop.contactTelephone || "Not provided"}</dd>
              </div>
              <div>
                <dt>Access instructions</dt>
                <dd>{stop.accessInstructions || "Not provided"}</dd>
              </div>
              <div>
                <dt>Opening / access times</dt>
                <dd>{stop.openingHours || "Not provided"}</dd>
              </div>
              <div>
                <dt>Stop instructions</dt>
                <dd>{stop.instructions || "Not provided"}</dd>
              </div>
              {stop.arrivedAt && (
                <div>
                  <dt>Arrived</dt>
                  <dd>{new Date(stop.arrivedAt).toLocaleString("en-GB")}</dd>
                </div>
              )}
              {stop.outcomeAt && (
                <div>
                  <dt>Outcome recorded</dt>
                  <dd>{new Date(stop.outcomeAt).toLocaleString("en-GB")}</dd>
                </div>
              )}
              {stop.outcomeReason && (
                <div>
                  <dt>Outcome reason</dt>
                  <dd>{stop.outcomeReason}</dd>
                </div>
              )}
              {stop.evidenceNotes && (
                <div>
                  <dt>Evidence notes</dt>
                  <dd>{stop.evidenceNotes}</dd>
                </div>
              )}
            </dl>
          </div>
        </article>
      ))}
    </section>
  );
}
function money(value: number) {
  return value.toLocaleString("en-GB", {
    style: "currency",
    currency: "GBP",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}
function discoveryMatches(load: LoadSummary, params: URLSearchParams) {
  const has = (value: string | undefined, needle: string) =>
    !needle || value?.toLowerCase().includes(needle.toLowerCase());
  const q = (params.get("q") ?? "").trim(),
    from = (params.get("from") ?? "").replace(/\s/g, "").toUpperCase(),
    to = (params.get("to") ?? "").replace(/\s/g, "").toUpperCase(),
    group = params.get("group") ?? "",
    poster = params.get("poster") ?? "",
    when = params.get("when") ?? "";
  if (group && !load.groups.some((item) => has(item.name, group))) return false;
  if (poster && !has(load.posterTradingName, poster)) return false;
  if (when && load.timescaleKind !== when) return false;
  if (
    from &&
    !load.stops[0]?.postcode.replace(/\s/g, "").toUpperCase().startsWith(from)
  )
    return false;
  if (
    to &&
    !load.stops.at(-1)?.postcode.replace(/\s/g, "").toUpperCase().startsWith(to)
  )
    return false;
  if (
    q &&
    !has(
      [
        load.description,
        load.publicNotes,
        load.posterTradingName,
        load.requiredVehicleClass,
        load.requiredBodyType,
        load.timescaleText,
        ...load.stops.map((stop) => stop.postcode),
        ...load.groups.map((item) => item.name),
      ].join(" "),
      q,
    )
  )
    return false;
  const radius = Number(params.get("radius"));
  if (radius > 0 && params.get("near")) {
    const origin = storedCoordinate("loadsub:search-origin"),
      collection = postcodeCoordinate(load.stops[0]?.postcode);
    if (origin && collection && distanceMiles(origin, collection) > radius)
      return false;
  }
  return true;
}
function discoverySort(params: URLSearchParams) {
  const sort = params.get("sort");
  return (a: LoadSummary, b: LoadSummary) => {
    if (sort === "price")
      return (
        (a.fixedPaymentPence ?? Number.MAX_SAFE_INTEGER) -
        (b.fixedPaymentPence ?? Number.MAX_SAFE_INTEGER)
      );
    if (sort === "mileage")
      return (
        (a.posterMileage ?? Number.MAX_SAFE_INTEGER) -
        (b.posterMileage ?? Number.MAX_SAFE_INTEGER)
      );
    if (sort === "rate") return rate(a) - rate(b);
    if (sort === "distance") {
      const origin = storedCoordinate("loadsub:search-origin"),
        ac = postcodeCoordinate(a.stops[0]?.postcode),
        bc = postcodeCoordinate(b.stops[0]?.postcode);
      if (origin && ac && bc)
        return distanceMiles(origin, ac) - distanceMiles(origin, bc);
    }
    return (
      new Date(b.publishedAt).getTime() - new Date(a.publishedAt).getTime()
    );
  };
}
function rate(load: LoadSummary) {
  return load.fixedPaymentPence && load.posterMileage
    ? load.fixedPaymentPence / load.posterMileage
    : Number.MAX_SAFE_INTEGER;
}
type Coordinate = { lat: number; lon: number };
function storedCoordinate(key: string): Coordinate | null {
  try {
    const value = JSON.parse(sessionStorage.getItem(key) ?? "null");
    return Number.isFinite(value?.lat) && Number.isFinite(value?.lon)
      ? value
      : null;
  } catch {
    return null;
  }
}
function postcodeCoordinate(postcode: string | undefined): Coordinate | null {
  if (!postcode) return null;
  const key = `loadsub:postcode:${postcode.replace(/\s/g, "").toUpperCase()}`,
    stored = storedCoordinate(key);
  if (stored) return stored;
  if (sessionStorage.getItem(`${key}:loading`)) return null;
  sessionStorage.setItem(`${key}:loading`, "1");
  void fetch(
    `https://api.postcodes.io/postcodes/${encodeURIComponent(postcode)}`,
  )
    .then((response) => response.json())
    .then((data: any) => {
      if (data?.result)
        sessionStorage.setItem(
          key,
          JSON.stringify({
            lat: data.result.latitude,
            lon: data.result.longitude,
          }),
        );
    })
    .finally(() => {
      sessionStorage.removeItem(`${key}:loading`);
      window.dispatchEvent(new PopStateEvent("popstate"));
    });
  return null;
}

async function estimateRouteMiles(postcodes: string[]) {
  const normal = postcodes
    .map((value) => value.replace(/\s/g, "").toUpperCase())
    .filter(Boolean);
  if (normal.length < 2) throw new Error("ROUTE_REQUIRED");
  const key = `loadsub:route-mileage:${normal.join("-")}`,
    cached = sessionStorage.getItem(key);
  if (cached) return Number(cached);
  const coordinates = await Promise.all(normal.map(lookupPostcode));
  let miles = 0;
  for (let index = 1; index < coordinates.length; index++)
    miles += distanceMiles(coordinates[index - 1]!, coordinates[index]!);
  const approximate = Math.max(1, Math.round(miles * 1.18));
  sessionStorage.setItem(key, String(approximate));
  return approximate;
}
async function lookupPostcode(postcode: string): Promise<Coordinate> {
  const key = `loadsub:postcode:${postcode}`,
    cached = storedCoordinate(key);
  if (cached) return cached;
  const response = await fetch(
      `https://api.postcodes.io/postcodes/${encodeURIComponent(postcode)}`,
    ),
    data = (await response.json()) as any;
  if (!response.ok || !data.result) throw new Error("POSTCODE_NOT_FOUND");
  const coordinate = { lat: data.result.latitude, lon: data.result.longitude };
  sessionStorage.setItem(key, JSON.stringify(coordinate));
  return coordinate;
}
function distanceMiles(a: Coordinate, b: Coordinate) {
  const radians = (value: number) => (value * Math.PI) / 180,
    dLat = radians(b.lat - a.lat),
    dLon = radians(b.lon - a.lon),
    x =
      Math.sin(dLat / 2) ** 2 +
      Math.cos(radians(a.lat)) *
        Math.cos(radians(b.lat)) *
        Math.sin(dLon / 2) ** 2;
  return 3958.8 * 2 * Math.atan2(Math.sqrt(x), Math.sqrt(1 - x));
}
function Select({
  label,
  value,
  values,
  set,
}: {
  label: string;
  value: string | undefined;
  values: string[];
  set: (v: string | undefined) => void;
}) {
  return (
    <label>
      <span>
        {label} <small>optional</small>
      </span>
      <select
        className="form-select"
        value={value ?? ""}
        onChange={(e) => set(e.target.value || undefined)}
      >
        {values.map((v) => (
          <option key={v} value={v}>
            {v || "Not specified"}
          </option>
        ))}
      </select>
    </label>
  );
}
function MinimumVehicleSelect({
  maximum,
  value,
  set,
}: {
  maximum: string | undefined;
  value: string | undefined;
  set: (v: string | undefined) => void;
}) {
  function tooLarge(candidate: string) {
    if (!maximum || !candidate || candidate === maximum) return false;
    const maximumRank = vehicleSizeLadder.indexOf(maximum);
    const candidateRank = vehicleSizeLadder.indexOf(candidate);
    return maximumRank < 0 || candidateRank < 0 || candidateRank > maximumRank;
  }
  return (
    <label>
      <span>
        Minimum vehicle <small>optional</small>
      </span>
      <select
        className="form-select"
        value={value ?? ""}
        onChange={(event) => set(event.target.value || undefined)}
      >
        {vehicleClasses.map((candidate) => (
          <option
            key={candidate}
            value={candidate}
            disabled={tooLarge(candidate)}
          >
            {candidate || "Not specified"}
          </option>
        ))}
      </select>
    </label>
  );
}
function MaximumVehicleSelect({
  minimum,
  value,
  set,
}: {
  minimum: string | undefined;
  value: string | undefined;
  set: (v: string | undefined) => void;
}) {
  function tooSmall(candidate: string) {
    if (!minimum || !candidate || candidate === minimum) return false;
    const minimumRank = vehicleSizeLadder.indexOf(minimum);
    const candidateRank = vehicleSizeLadder.indexOf(candidate);
    return minimumRank < 0 || candidateRank < 0 || candidateRank < minimumRank;
  }
  return (
    <label>
      <span>
        Maximum vehicle <small>optional</small>
      </span>
      <select
        className="form-select"
        value={value ?? ""}
        onChange={(event) => set(event.target.value || undefined)}
      >
        {vehicleClasses.map((candidate) => (
          <option
            key={candidate}
            value={candidate}
            disabled={tooSmall(candidate)}
          >
            {candidate || "Not specified"}
          </option>
        ))}
      </select>
    </label>
  );
}
function Num({
  label,
  value,
  set,
}: {
  label: string;
  value: number | undefined;
  set: (v: number | undefined) => void;
}) {
  return (
    <label>
      <span>
        {label} <small>optional</small>
      </span>
      <input
        className="form-control"
        type="number"
        min="0.1"
        step="0.1"
        value={value ?? ""}
        onChange={(e) =>
          set(e.target.value ? Number(e.target.value) : undefined)
        }
      />
    </label>
  );
}
function message(e: unknown) {
  return e instanceof ApiError
    ? e.message
    : "Unable to reach LoadSub. Please try again.";
}
function TrailerStopOperations({
  stop,
  set,
}: {
  stop: LoadStopInput;
  set: (value: LoadStopInput) => void;
}) {
  const operations = stop.operations ?? [];
  const labels: Record<string, string> = {
    live_load: "Live load",
    tip_unload: "Tip / unload",
    reload: "Reload",
    drop_trailer: "Drop trailer",
    collect_trailer: "Collect trailer",
    swap_trailer: "Swap trailers",
    couple: "Couple",
    uncouple: "Uncouple",
    other: "Other",
  };
  function update(
    index: number,
    value: NonNullable<LoadStopInput["operations"]>[number],
  ) {
    const next = [...operations];
    next[index] = value;
    set({ ...stop, operations: next });
  }
  return (
    <div className="stop-operations">
      <strong>Trailer actions</strong>
      {operations.map((operation, index) => (
        <div className="load-form-grid" key={index}>
          <label>
            <span>Action</span>
            <select
              className="form-select"
              value={operation.action}
              onChange={(event) =>
                update(index, {
                  ...operation,
                  action: event.target.value as typeof operation.action,
                })
              }
            >
              {Object.entries(labels).map(([value, label]) => (
                <option value={value} key={value}>
                  {label}
                </option>
              ))}
            </select>
          </label>
          {[
            "collect_trailer",
            "swap_trailer",
            "couple",
            "drop_trailer",
          ].includes(operation.action) && (
            <label>
              <span>
                Trailer number <small>if known</small>
              </span>
              <input
                className="form-control text-uppercase"
                value={operation.trailerIdentifier ?? ""}
                onChange={(event) =>
                  update(index, {
                    ...operation,
                    trailerIdentifier: event.target.value || undefined,
                  })
                }
              />
            </label>
          )}
          <label>
            <span>Loaded state</span>
            <select
              className="form-select"
              value={operation.loadedState ?? "unknown"}
              onChange={(event) =>
                update(index, {
                  ...operation,
                  loadedState: event.target.value as
                    "loaded" | "empty" | "unknown",
                })
              }
            >
              <option value="unknown">Unknown</option>
              <option value="loaded">Loaded</option>
              <option value="empty">Empty</option>
            </select>
          </label>
          <label>
            <span>
              Instructions <small>optional</small>
            </span>
            <input
              className="form-control"
              value={operation.notes ?? ""}
              onChange={(event) =>
                update(index, {
                  ...operation,
                  notes: event.target.value || undefined,
                })
              }
            />
          </label>
          <button
            type="button"
            className="btn btn-sm btn-outline-danger"
            onClick={() =>
              set({
                ...stop,
                operations: operations.filter((_, item) => item !== index),
              })
            }
          >
            Remove action
          </button>
        </div>
      ))}
      <button
        type="button"
        className="btn btn-sm btn-outline-light"
        onClick={() =>
          set({
            ...stop,
            operations: [
              ...operations,
              { action: "other", loadedState: "unknown" },
            ],
          })
        }
      >
        Add trailer action
      </button>
    </div>
  );
}
